import { Box, DomHelpers, Help } from '@salesperformanceint/e4shared';
import { ratingColour } from 'helpers/metrics';
import { getPermissions } from 'helpers/permissions';
import React from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import posthog from 'posthog-js';
import PositiveIndicators from 'components/user/positiveIndicators';
import MetricsTable from '../../../metricsTable';
import CompetencyRoleDescriptions from 'views/user/components/competencyRoleDescriptions';
import { useDispatch } from 'react-redux';
import { toggleExploreAside } from 'actions/ui';
import { translate, translateOverride } from 'helpers/translations';
import { hasModule } from 'helpers/profile';

export const PerformanceCompetencyCard = ({
    history,
    profile,
    text,
    reloading,
    onFlag,
    show,
    basic,
    competency: entity,
}) => {
    const dispatch = useDispatch();
    const loaded = React.useRef(null);
    const { coaching, downline, user } = useSelector(state => state.user);
    const [permissions, setPermissions] = React.useState(getPermissions());
    const ratings = user.companySettings?.display?.ratingRange || 5;
    const flagHelp = !permissions.one2ones.canFlag
        ? entity.flagged
            ? text.behaviours.card.flag.areaForAttention
            : ''
        : entity.flagged
          ? text.behaviours.card.flag.removeFlag
          : text.behaviours.card.flag.addFlag;

    const resizeHandler = () => loaded?.current && DomHelpers.truncateText('truncate-text', loaded.current, 0);

    React.useEffect(() => {
        resizeHandler();
    }, [loaded.current]);

    React.useEffect(() => {
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []);

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            setPermissions(getPermissions(profile, user, coaching, downline));
        }
    }, [profile?.id, user?.id]);

    entity.name = translate(user.language_Code, entity, 'Name');
    entity.description = translate(user.language_Code, entity, 'Description');
    entity.positive_Indicators?.forEach(pi => {
        pi.statement = translate(user.language_Code, pi, 'Statement');
    });

    entity = translateOverride(user.language_Code, entity);

    const buttonClick = React.useCallback(
        e => {
            const btn = DomHelpers.getEventButton(e);
            const value = btn.value;

            switch (value) {
                case 'competency-add':
                    show(value, entity);
                    posthog.capture('behaviour-add-objective', { behaviour: entity.name });
                    break;
                case 'competency-line':
                    show(value, entity);
                    posthog.capture('behaviour-competency-over-time', { behaviour: entity.name });
                    break;
                case 'competency-tasks':
                    show(value, entity);
                    posthog.capture('behaviour-view-objective', { behaviour: entity.name });
                    break;
                case 'behaviour-search':
                    dispatch(
                        toggleExploreAside({
                            profileId: profile.id,
                            competencyId: entity.id,
                            competency: entity.name,
                        })
                    );
                    posthog.capture('behaviour-search', { behaviour: entity.name });
                    break;
                default:
                    break;
            }
        },
        [entity, profile]
    );

    return (
        <Box className={`behaviour-box box-${ratingColour(entity.rating)}`}>
            <div className="flex flex-center" ref={loaded}>
                <h4
                    className="flex-grow truncate-text ellipse"
                    data-testid="behaviour-card-title"
                    data-area-id={entity.area_Id}
                    data-competency-id={entity.id}
                >
                    {entity.name}
                </h4>
                {onFlag && (
                    <>
                        {permissions.one2ones.canFlag ? (
                            <button
                                data-testid="btn-behaviour-flag"
                                title={flagHelp}
                                className="btn btn-link ml10"
                                onClick={() => {
                                    onFlag(entity);
                                    posthog.capture('behaviour-flag-clicked', { behaviour: entity.name });
                                }}
                            >
                                <i className={`mr10 fa fa-flag ${entity.flagged ? 'text-danger fa-solid' : 'text-grey'}`} />
                            </button>
                        ) : (
                            <i className={`mr10 fa fa-flag ${entity.flagged ? 'text-danger fa-solid' : 'text-grey'}`} />
                        )}
                    </>
                )}
            </div>
            <div className="behaviour-sync">
                {entity.roleDescriptions?.length > 0 && entity.roleDescriptions[0].description ? (
                    <p>{entity.roleDescriptions[0].description}</p>
                ) : entity.description ? (
                    <p>{entity.description}</p>
                ) : (
                    <p>
                        <span className="invisible">No description set</span>
                    </p>
                )}
                {!basic && entity.metrics?.length > 0 && (
                    <MetricsTable
                        key={`metrics-table-${entity.id}-${profile.id}`}
                        id={entity.id}
                        metrics={[...entity.metrics]}
                        text={text}
                        reloading={reloading}
                        show={show}
                        pageSize={4}
                    />
                )}
            </div>
            <div className="flex flex-center mt20 mb10">
                <div className="flex-grow">
                    {entity && (
                        <button
                            data-testid="btn-behaviour-search"
                            id="behaviour-search-button"
                            title={
                                entity.courses_InProgress > 0 ? text.behaviours.card.continue : text.behaviours.card.explore
                            }
                            className="btn btn-primary"
                            value="behaviour-search"
                            onClick={buttonClick}
                        >
                            {entity.courses_InProgress > 0 ? text.behaviours.card.continue : text.behaviours.card.explore}
                        </button>
                    )}
                </div>
                <div className="table-btns">
                    <span
                        className={`no-hover btn btn-xs btn-rating btn-${ratingColour(entity.rating)}`}
                        title={text.behaviours.card.rating}
                    >
                        {entity.rating}/{ratings}
                    </span>
                    {entity?.positive_Indicators?.length > 0 ? (
                        <Help
                            id={`help-behaviour-${entity.id}`}
                            target={
                                <span className="btn btn-xs btn-primary">
                                    <i className="fa fa-info-circle" />
                                </span>
                            }
                        >
                            <PositiveIndicators positiveIndicators={entity.positive_Indicators} />
                        </Help>
                    ) : entity.description ? (
                        <Help
                            id={`help-competency-${entity.id}`}
                            target={
                                <span className="btn btn-xs btn-primary">
                                    <i className="fa fa-info-circle" />
                                </span>
                            }
                        >
                            <CompetencyRoleDescriptions descriptions={entity.roleDescriptions} />
                        </Help>
                    ) : (
                        <button disabled className="btn btn-xs btn-primary">
                            <i className="fa fa-info-circle" />
                        </button>
                    )}
                    <button
                        data-testid="btn-behaviour-competency-over-time"
                        className="btn btn-xs btn-primary"
                        title={text.all.card.competencyOvertime}
                        value="competency-line"
                        onClick={buttonClick}
                    >
                        <i className="fa fa-line-chart" />
                    </button>
                    {hasModule(user, 'objectives') && (
                        <button
                            data-testid="btn-behaviour-add-objective"
                            className="btn btn-xs btn-primary"
                            title={text.behaviours.card.addObjective}
                            value="competency-add"
                            onClick={buttonClick}
                        >
                            <i className="fa fa-plus-circle" />
                        </button>
                    )}
                    <span className="btn-badge">
                        <button
                            data-testid="btn-behaviour-tasks"
                            className="btn btn-xs btn-primary"
                            disabled={!entity.tasks_Count}
                            title={text.behaviours.card.objectives}
                            value="competency-tasks"
                            onClick={buttonClick}
                        >
                            <i className="fa fa-tasks" />
                        </button>
                        {entity.overdue_Tasks_Count > 0 && (
                            <span className="badge label label-danger hidden-xs" title={text.behaviours.card.overdue}>
                                {entity.overdue_Tasks_Count}
                            </span>
                        )}
                    </span>
                </div>
            </div>
        </Box>
    );
};

export default withRouter(PerformanceCompetencyCard);
